import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, apiFetch, API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ShareButtons } from '../components/ShareButtons';
import MainNav from '../components/MainNav';
import SongsSidebar from '../components/SongsSidebar';
import RenderingClipPlayer from '../components/RenderingClipPlayer';
import AddToPlaylistDialog from '../components/AddToPlaylistDialog';
import { highlightTriggers } from '../lib/sunoTriggers';
import { toast } from 'sonner';
import { 
  ArrowLeft, Music, Zap, Play, Pause, Volume2, VolumeX, 
  Download, Video, Loader2, RefreshCw, Clock, CheckCircle, AlertCircle, Pencil, Check, X, Lock, Unlock, Globe, GlobeLock, Library as LibraryIcon, Settings, Trash2, ExternalLink, ListMusic
} from 'lucide-react';

export default function SongDetail() {
  const { songId } = useParams();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const leftContentRef = useRef(null);
  
  const [song, setSong] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [sidebarHeight, setSidebarHeight] = useState(null);
  
  // Audio player state
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Edit title state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  
  // Sidebar refresh trigger
  const [sidebarRefresh, setSidebarRefresh] = useState(0);

  // v3.0.134 — Add-to-playlist dialog
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);

  // v3.0.129: fetch the current render cost so the Regenerate button
  // can preview it. Defaults to 2 credits if the fetch fails.
  const [renderCost, setRenderCost] = useState(2);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/credits/costs`);
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled && typeof d.song_render === 'number') setRenderCost(d.song_render);
      } catch { /* keep default */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Outro state — once a song finishes generating we want the rendering
  // clip player to finish its current clip and then play the outro before
  // the player unmounts. Without this, the player is destroyed the
  // instant `song.status` flips to 'completed' and the outro never plays.
  //
  // CRITICAL: we flip `outroPending` DURING render (the React-blessed
  // "derive state from props" pattern) rather than in a useEffect.
  // Doing it in useEffect creates a 1-frame gap where the JSX renders
  // null (status='completed' AND outroPending=false in the same commit),
  // the player unmounts, and then remounts as a fresh instance with a
  // restarted batch fetch — racing the immediate-outro logic and
  // skipping the outro entirely (bug observed in production 2026-06-30).
  const [outroPending, setOutroPending] = useState(false);
  const prevStatusRef = useRef(null);
  if (
    prevStatusRef.current === 'processing' &&
    song?.status &&
    song.status !== 'processing' &&
    !outroPending
  ) {
    setOutroPending(true);
  }
  prevStatusRef.current = song?.status;
  // Safety net: if the player never signals onAnimationDone (e.g.
  // video error, batch returned no clips), clear outroPending after
  // 30s so the completed-state UI isn't blocked indefinitely.
  useEffect(() => {
    if (!outroPending) return;
    const t = setTimeout(() => setOutroPending(false), 30000);
    return () => clearTimeout(t);
  }, [outroPending]);
  
  // Video creation state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoOrientation, setVideoOrientation] = useState('square');
  const [creatingVideo, setCreatingVideo] = useState(false);
  const [videoStatus, setVideoStatus] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [existingVideoUrl, setExistingVideoUrl] = useState(null);
  
  // Elapsed time counter for generating state
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Dynamic credit costs
  const [unlockCost, setUnlockCost] = useState(20);
  const [isDiscounted, setIsDiscounted] = useState(false);
  
  // Public library toggle state
  const [isPublic, setIsPublic] = useState(false);
  const [togglingPublic, setTogglingPublic] = useState(false);
  
  // Library sharing options state
  const [allowDownload, setAllowDownload] = useState(true);
  const [allowAdd, setAllowAdd] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [savingLibraryOptions, setSavingLibraryOptions] = useState(false);
  const [showLibrarySettings, setShowLibrarySettings] = useState(false);

  // Inline lyrics editor state (cover songs only). Lets users fix Whisper
  // transcription errors before clicking Render Another Variation. The
  // regen pipeline reads `song.lyrics` from the DB at click-time, so saved
  // edits flow into the next render automatically.
  const [editingLyrics, setEditingLyrics] = useState(false);
  const [lyricsDraft, setLyricsDraft] = useState('');

  // Admin-only "Recover this song" button — pulls audio from SUNO when our
  // song is marked failed but SUNO actually completed it. Saves having to
  // copy/paste the song_id into Admin Panel → Client Errors.
  const [recovering, setRecovering] = useState(false);

  // Variations dropdown — every song the user has manually regenerated
  // from this one. Useful for A/B comparing 3-4 takes of the same cover.
  const [variations, setVariations] = useState([]);
  const [showVariations, setShowVariations] = useState(false);

  const handleRecoverSong = async () => {
    if (recovering) return;

    // If the song has no stored task_id, the backend can't look it up itself.
    // Prompt the admin to paste the task_id from SUNO's dashboard.
    let manualTaskId = null;
    const hasStoredTaskId = !!(song?.suno_task_id || song?.task_id);
    if (!hasStoredTaskId) {
      const entered = window.prompt(
        'This song has no SUNO task_id stored.\n\nPaste the task_id from SUNO\'s dashboard (or leave blank to abort):',
        ''
      );
      if (!entered || !entered.trim()) {
        return; // user cancelled
      }
      manualTaskId = entered.trim();
    }

    setRecovering(true);
    try {
      const body = { song_id: song.song_id };
      if (manualTaskId) body.task_id = manualTaskId;
      const { data } = await apiFetch('/admin/recover-song', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (data?.result === 'recovered') {
        toast.success('Recovered from SUNO — reloading…');
        setTimeout(() => window.location.reload(), 800);
      } else {
        toast.info(data?.message || `Result: ${data?.result || 'unknown'}`);
      }
    } catch (err) {
      toast.error(err?.message || 'Recovery failed');
    } finally {
      setRecovering(false);
    }
  };

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await apiFetch('/library');
        setCategories(data.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const fetchSong = useCallback(async () => {
    try {
      const { data } = await apiFetch(`/songs/${songId}/status`);
      setSong(data);
      setIsPublic(data.is_public || false);
      setAllowDownload(data.allow_download !== false); // Default true
      setAllowAdd(data.allow_add !== false); // Default true
      setSelectedCategory(data.category || '');
      return data;
    } catch (error) {
      // v2.9.45: Do NOT trigger refreshUser on 401 here. This used to call
      // checkAuth() on every transient 401, and a single backend/Cloudflare
      // blip during the 10-second poll loop would clear the token and bounce
      // the user back to "/" mid-render. Background polls aren't strong
      // enough evidence to invalidate a 7-day session — just silently retry
      // on the next tick. If the session is actually dead, the user will hit
      // it on their next deliberate action.
      const status = error?.response?.status;
      if (status === 401) {
        return; // poll will retry in 10s
      }
      // v2.9.59 — Only navigate away on a genuine 404 (song doesn't exist).
      // 5xx / network errors during polling are usually transient (Cloudflare
      // 502s, mobile network blips, brief backend hiccups). Bouncing the
      // user mid-render is a way worse UX than just retrying on the next
      // tick. User reported 4 random bounces across 15-20 renders — this
      // path was the cause.
      if (status === 404) {
        toast.error('Song not found');
        navigate('/dashboard');
        return;
      }
      // Transient error — log it, but do NOT navigate. The 10s poll loop
      // will retry on the next tick. We deliberately suppress the toast
      // here because during a 4-5 min render the user might see 20+ poll
      // ticks; one bad tick shouldn't spam them.
      console.error('Error fetching song (transient, will retry):', status, error?.message);
    } finally {
      setLoading(false);
    }
  }, [songId, navigate]);

  useEffect(() => {
    // Gate on auth ready — same fix as Dashboard (v2.9.28) + Library + Sidebar.
    if (authLoading || !user) return;
    fetchSong();
    // Fetch credit costs
    const fetchCosts = async () => {
      try {
        const { data } = await apiFetch('/credits/costs');
        if (data?.song_unlock !== undefined) {
          setUnlockCost(data.song_unlock);
        }
      } catch (error) {
        console.error('Error fetching credit costs:', error);
      }
    };
    fetchCosts();
    
    // Fetch personalized unlock price for this song (may be discounted)
    const fetchUnlockPrice = async () => {
      try {
        const { data } = await apiFetch(`/songs/${songId}/unlock-price`);
        if (data && !data.already_unlocked) {
          setUnlockCost(data.price);
          setIsDiscounted(data.discounted);
        }
      } catch (error) {
        console.error('Error fetching unlock price:', error);
      }
    };
    fetchUnlockPrice();

    // Fetch any songs the user has manually regenerated from this one. Empty
    // is fine — we just hide the dropdown when count is 0. Best-effort: a
    // failure here shouldn't block the page from rendering.
    const fetchVariations = async () => {
      try {
        const { data } = await apiFetch(`/songs/${songId}/variations`);
        setVariations(data?.variations || []);
      } catch (e) {
        console.debug('variations fetch failed:', e);
        // silent — dropdown stays hidden
      }
    };
    fetchVariations();
    
    // Fetch existing video for this song
    const fetchExistingVideo = async () => {
      try {
        const { data } = await apiFetch(`/songs/${songId}/video`);
        if (data?.video?.video_url) {
          setExistingVideoUrl(data.video.video_url);
        }
      } catch (error) {
        // No video yet, that's fine
        console.log('No existing video for song');
      }
    };
    fetchExistingVideo();
  }, [fetchSong, songId, authLoading, user]);

  // Poll for status if processing
  useEffect(() => {
    if (!song || song.status !== 'processing') return;
    
    const pollInterval = setInterval(async () => {
      const data = await fetchSong();
      if (data?.status === 'completed' || data?.status === 'failed') {
        clearInterval(pollInterval);
        setSidebarRefresh(prev => prev + 1); // Refresh sidebar when status changes
      }
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [song?.status, fetchSong]);

  // Elapsed time counter for processing songs
  useEffect(() => {
    if (!song || song.status !== 'processing') {
      setElapsedTime(0);
      return;
    }
    
    // Calculate initial elapsed time from song creation
    const createdAt = new Date(song.created_at);
    const initialElapsed = Math.floor((Date.now() - createdAt.getTime()) / 1000);
    setElapsedTime(initialElapsed > 0 ? initialElapsed : 0);
    
    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [song?.status, song?.created_at]);

  // Format elapsed time as MM:SS
  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleRefresh = async () => {
    setPolling(true);
    const updatedSong = await fetchSong();
    setSidebarRefresh(prev => prev + 1);
    setPolling(false);
    
    // Show feedback to user
    if (updatedSong?.status === 'completed') {
      toast.success('Song is ready!');
    } else if (updatedSong?.status === 'failed') {
      // Show the friendly Suno failure message (e.g. content-filter rejection)
      toast.error(updatedSong?.error || 'Song generation failed', { duration: 10000 });
    } else {
      toast.info('Still processing... check back in a moment');
    }
  };

  const handleEditTitle = () => {
    setEditedTitle(song?.title || 'Untitled');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!editedTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }
    
    setIsSavingTitle(true);
    try {
      await apiFetch(`/songs/${songId}/title`, {
        method: 'PUT',
        body: JSON.stringify({ title: editedTitle.trim() })
      });
      setSong({ ...song, title: editedTitle.trim() });
      setIsEditingTitle(false);
      toast.success('Title updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update title');
    } finally {
      setIsSavingTitle(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingTitle(false);
    setEditedTitle('');
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleRegenerate = async () => {
    // Navigate to CreateSong with the current song's data pre-filled so the
    // user can tweak lyrics / style / voice / model before the new render
    // fires. Applies to BOTH covers and non-covers — previously covers
    // submitted instantly with no chance to edit, which meant fixing a typo
    // in the lyrics meant abandoning the source MP3 and starting from scratch.
    // For covers we also pass `cover_audio_url` so CreateSong can pre-flag
    // Cover Mode and skip the upload step.
    navigate('/create-song', {
      state: {
        prefillData: {
          lyrics: song.lyrics,
          prompt: song.description || song.prompt,  // Prefer description if available
          title: song.title,
          genre: song.genre,
          mood: song.mood,
          tempo: song.tempo,
          voice: song.voice,
          // v2.9.35 — Carry the original song's engine into the regenerate
          // flow so MusicGPT songs regenerate on MusicGPT, Suno songs on
          // Suno. User can still flip the dropdown before submitting.
          engine: song.engine || 'suno',
          isRegeneration: true,
          originalSongId: song.song_id,
          // Cover-specific fields — CreateSong toggles coverMode + reuses the
          // existing source MP3 instead of forcing the user to re-upload.
          isCover: !!song.is_cover,
          coverAudioUrl: song.cover_source_url || null,
        }
      }
    });
  };

  // Measure left content height for dynamic sidebar sizing.
  // Floor the value so songs without lyrics (which leave the left column
  // very short) don't clip the sidebar to a tiny strip.
  useEffect(() => {
    const measureHeight = () => {
      if (leftContentRef.current) {
        const measured = leftContentRef.current.offsetHeight;
        // 70vh keeps the song list comfortably tall on any viewport;
        // never go below 600px so small laptops still see ~5 rows.
        const viewportFloor = typeof window !== 'undefined'
          ? Math.max(600, Math.round(window.innerHeight * 0.7))
          : 600;
        setSidebarHeight(Math.max(measured, viewportFloor));
      }
    };
    
    measureHeight();
    window.addEventListener('resize', measureHeight);
    return () => window.removeEventListener('resize', measureHeight);
  }, [song, isPlaying]);

  // Handle video creation for social media
  const handleCreateVideo = async () => {
    setCreatingVideo(true);
    try {
      const { data } = await apiFetch(`/songs/${songId}/create-video`, {
        method: 'POST',
        body: JSON.stringify({ song_id: songId, orientation: videoOrientation })
      });
      setVideoId(data.video_id);
      setVideoStatus({ status: 'pending' });
      toast.success('Video generation started!');
    } catch (err) {
      toast.error(err.message || 'Failed to create video');
      setCreatingVideo(false);
    }
  };

  // Poll for video status
  useEffect(() => {
    if (!videoId) return;
    
    const pollVideo = async () => {
      try {
        const { data } = await apiFetch(`/slideshow/${videoId}`);
        setVideoStatus(data);
        
        if (data.status === 'completed') {
          setCreatingVideo(false);
          setExistingVideoUrl(data.video_url); // Update existing video URL
          toast.success('Video ready for download!');
        } else if (data.status === 'failed') {
          setCreatingVideo(false);
          toast.error(data.error || 'Video generation failed');
        }
      } catch (err) {
        console.error('Failed to poll video status:', err);
      }
    };
    
    const interval = setInterval(pollVideo, 3000);
    pollVideo();
    
    return () => clearInterval(interval);
  }, [videoId]);

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const { data } = await apiFetch(`/songs/${songId}/unlock`, { method: 'POST' });
      toast.success(data.message);
      refreshUser();
      fetchSong(); // Refresh song data
      setSidebarRefresh(prev => prev + 1); // Trigger sidebar refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Unlock failed');
    } finally {
      setUnlocking(false);
    }
  };

  const handleTogglePublic = async () => {
    setTogglingPublic(true);
    try {
      const { data } = await apiFetch(`/songs/${songId}/public`, { method: 'PUT' });
      setIsPublic(data.is_public);
      toast.success(data.message);
      if (!data.is_public) {
        setShowLibrarySettings(false); // Close settings when making private
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update public status');
    } finally {
      setTogglingPublic(false);
    }
  };

  const handleSaveLibraryOptions = async () => {
    // Validate category is selected
    if (!selectedCategory) {
      toast.error('Please select a category for the library');
      return;
    }
    
    setSavingLibraryOptions(true);
    try {
      const { data } = await apiFetch(`/songs/${songId}/library-options`, {
        method: 'PUT',
        body: JSON.stringify({
          allow_download: allowDownload,
          allow_add: allowAdd,
          category: selectedCategory
        })
      });
      toast.success('Library options saved!');
      setShowLibrarySettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save options');
    } finally {
      setSavingLibraryOptions(false);
    }
  };

  const handleRemoveFromLibrary = async () => {
    if (!window.confirm('Remove this song from the public library? Users who already added it will keep their copies.')) {
      return;
    }
    setTogglingPublic(true);
    try {
      const { data } = await apiFetch(`/songs/${songId}/public`, { method: 'PUT' });
      setIsPublic(false);
      setShowLibrarySettings(false);
      toast.success('Song removed from public library');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove from library');
    } finally {
      setTogglingPublic(false);
    }
  };

  // Limit playback to 50% if locked
  useEffect(() => {
    if (song?.locked && audioRef.current && duration > 0) {
      const maxTime = duration * 0.5;
      if (currentTime >= maxTime) {
        audioRef.current.pause();
        setIsPlaying(false);
        toast.info('Preview limit reached. Unlock to hear the full song!');
      }
    }
  }, [currentTime, duration, song?.locked]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            {[0, 0.1, 0.2, 0.3, 0.4].map((delay) => (
              <div key={`bar-${delay}`} className="audio-bar" style={{ animationDelay: `${delay}s` }}></div>
            ))}
          </div>
          <span className="text-white">Loading song...</span>
        </div>
      </div>
    );
  }

  if (!song) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <MainNav />

      {/* Main Content with Sidebar */}
      <main className="pt-24 pb-32 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto flex gap-8">
          {/* Main Content */}
          <div ref={leftContentRef} className="flex-1" data-testid="song-detail">
            {/* Song Header */}
            <div className="flex flex-col md:flex-row gap-8 mb-8">
              {/* Album Art */}
              <div className="w-full md:w-64 shrink-0">
                <div className="aspect-square rounded-xl overflow-hidden bg-gradient-to-br from-primary/20 to-secondary/20">
                  {song.image_url ? (
                    <img src={song.image_url} alt={song.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="h-16 w-16 text-white/20" />
                    </div>
              )}
            </div>
          </div>

          {/* Song Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              {getStatusIcon(song.status)}
              <span className="text-sm text-zinc-400 capitalize">{song.status}</span>
            </div>
            
            {/* Editable Title */}
            {isEditingTitle ? (
              <div className="flex items-center gap-2 mb-2">
                <Input
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="input-dark text-2xl font-bold max-w-md"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle();
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  data-testid="edit-title-input"
                />
                <button
                  onClick={handleSaveTitle}
                  disabled={isSavingTitle}
                  className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 transition-colors"
                  data-testid="save-title-btn"
                >
                  {isSavingTitle ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />}
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                  data-testid="cancel-edit-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 mb-2 group">
                <h1 className="text-3xl font-bold text-white">{song.title || 'Untitled'}</h1>
                <button
                  onClick={handleEditTitle}
                  className="p-2 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded-lg text-zinc-400 hover:text-white transition-all"
                  title="Edit title"
                  data-testid="edit-title-btn"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
            )}
            
            {/* Show style description only */}
            {(song.description || song.prompt) && (
              <p className="text-zinc-400 mb-4">{song.description || song.prompt}</p>
            )}

            {/* "View original" — appears on any song that was created via
                manual regenerate (Render Another Variation / Regenerate New
                Version). Helps with A/B comparing edits. The parent might be
                another regenerated version too — that's fine, the link still
                walks one level up at a time. */}
            {song.original_song_id && song.original_song_id !== song.song_id && (
              <p className="text-sm text-zinc-500 mb-4">
                Regenerated from{' '}
                <button
                  onClick={() => navigate(`/song/${song.original_song_id}`)}
                  className="text-emerald-400 hover:text-emerald-300 hover:underline inline-flex items-center gap-1"
                  data-testid="view-original-link"
                >
                  <ExternalLink className="h-3 w-3" />
                  View original
                </button>
              </p>
            )}

            {/* Variations dropdown — every song manually regenerated FROM this
                one. Hidden when this song has zero variations. Lets the user
                A/B compare different takes of the same cover. */}
            {variations.length > 0 && (
              <div className="mb-4 relative inline-block" data-testid="variations-container">
                <button
                  onClick={() => setShowVariations((v) => !v)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-zinc-300"
                  data-testid="variations-toggle-btn"
                >
                  <RefreshCw className="h-3.5 w-3.5 text-emerald-400" />
                  {variations.length} {variations.length === 1 ? 'variation' : 'variations'}
                  <span className="text-zinc-500 text-xs">{showVariations ? '▾' : '▸'}</span>
                </button>

                {showVariations && (
                  <div className="absolute z-10 mt-2 w-80 max-h-80 overflow-y-auto bg-zinc-900 border border-white/10 rounded-lg shadow-xl p-2" data-testid="variations-dropdown">
                    {variations.map((v) => {
                      let when = v.created_at;
                      try { when = new Date(v.created_at).toLocaleString(); } catch (e) { console.debug('date parse failed:', e); /* keep raw */ }
                      return (
                        <button
                          key={v.song_id}
                          onClick={() => { setShowVariations(false); navigate(`/song/${v.song_id}`); }}
                          className="w-full text-left px-3 py-2 rounded hover:bg-white/5 flex items-center gap-3"
                          data-testid={`variation-${v.song_id}`}
                        >
                          {v.image_url ? (
                            <img src={v.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                              <Music className="h-4 w-4 text-zinc-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-zinc-200 truncate">{v.title || 'Untitled'}</div>
                            <div className="text-xs text-zinc-500 flex items-center gap-2">
                              <span className={`inline-block w-1.5 h-1.5 rounded-full ${
                                v.status === 'completed' ? 'bg-emerald-400'
                                : v.status === 'failed' ? 'bg-rose-400'
                                : 'bg-amber-400'
                              }`} />
                              <span>{v.status || 'unknown'}</span>
                              <span>·</span>
                              <span>{when}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            
            {/* Hide tags section - content is already shown in description above */}

            {/* SUNO single-version transparency banner.
                When SUNO returns only one variation (a known intermittent
                upstream behaviour), surface it so users understand they
                didn't lose anything. No credit cost is involved here —
                generation is free, only unlock spends credits. */}
            {song.status === 'completed' && song.audio_url && song.has_alt_variation === false && song.variation === 1 && (
              <div
                className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm flex items-start gap-2"
                data-testid="suno-single-version-banner"
              >
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p className="leading-relaxed">
                  <span className="font-semibold">Heads up:</span> SUNO only delivered one version of this song this time — usually it returns two. This is an occasional upstream behaviour from SUNO (not us). Tap <span className="font-medium">Regenerate</span> below if you'd like to try again.
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-4">
              {song.status === 'completed' && song.audio_url && (
                <>
                  {/* Lock/Unlock Badge and Button */}
                  {song.locked ? (
                    <Button
                      onClick={handleUnlock}
                      disabled={unlocking}
                      className={`${isDiscounted ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30' : 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30'}`}
                      data-testid="unlock-btn"
                    >
                      {unlocking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />}
                      {isDiscounted ? `Unlock for only ${unlockCost} credits!` : `Unlock Full Song (${unlockCost} credits)`}
                    </Button>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-full text-sm">
                      <Unlock className="h-3.5 w-3.5" />
                      Unlocked
                    </span>
                  )}
                  
                  {/* Public Library Toggle - only show for unlocked songs that are NOT from the library */}
                  {!song.locked && !song.added_from_library && (
                    <>
                      {!isPublic ? (
                        <Button
                          onClick={handleTogglePublic}
                          disabled={togglingPublic}
                          className="bg-zinc-700/50 hover:bg-zinc-700 text-zinc-300 border border-zinc-600"
                          data-testid="toggle-public-btn"
                        >
                          {togglingPublic ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <GlobeLock className="h-4 w-4 mr-2" />
                          )}
                          Make Public
                        </Button>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-full text-sm border border-purple-500/30">
                            <Globe className="h-3.5 w-3.5" />
                            Public
                          </span>
                          <Button
                            onClick={() => setShowLibrarySettings(!showLibrarySettings)}
                            variant="outline"
                            size="sm"
                            className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                            data-testid="library-settings-btn"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Show badge for songs added from library */}
                  {song.added_from_library && (
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-full text-sm border border-blue-500/30">
                      <LibraryIcon className="h-3.5 w-3.5" />
                      From Library
                    </span>
                  )}
                  
                  <ShareButtons 
                    type="song" 
                    id={song.song_id} 
                    title={song.title}
                    videoUrl={existingVideoUrl || (videoStatus?.status === 'completed' ? videoStatus.video_url : null)}
                    onCreateVideo={() => setShowVideoModal(true)}
                  />
                  
                  {/* Download button - only for unlocked songs */}
                  {!song.locked ? (
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/songs/${song.song_id}/download-with-cover`}
                      download={`${song.title || 'song'}.mp3`}
                      className="btn-secondary px-4 py-2 rounded-full inline-flex items-center"
                      data-testid="download-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </a>
                  ) : (
                    <Button
                      variant="outline"
                      disabled
                      className="border-white/10 text-zinc-500 cursor-not-allowed"
                      title="Unlock song to download"
                      data-testid="download-locked-btn"
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      Download (Locked)
                    </Button>
                  )}
                  
                  <Button
                    onClick={() => setShowVideoModal(true)}
                    className="bg-yellow-800 hover:bg-yellow-900 text-white px-4 py-2 rounded-full"
                    data-testid="create-video-btn"
                  >
                    <Video className="h-4 w-4 mr-2" />
                    Create Video
                  </Button>

                  {/* v3.0.134 — Add to Playlist */}
                  <Button
                    variant="outline"
                    onClick={() => setShowPlaylistDialog(true)}
                    className="border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 hover:text-white px-4 py-2 rounded-full"
                    data-testid="add-to-playlist-btn"
                  >
                    <ListMusic className="h-4 w-4 mr-2" />
                    Add to Playlist
                  </Button>
                </>
              )}
              
              {/* Regenerate Button - show for failed or no-audio songs */}
              {(song.status === 'failed' || (song.status === 'completed' && !song.audio_url)) && (
                <Button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="btn-primary"
                  data-testid="regenerate-btn"
                >
                  {regenerating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Regenerate
                  <span className="ml-2 text-sm opacity-80">
                    • {renderCost} credit{renderCost === 1 ? '' : 's'}
                  </span>
                </Button>
              )}

              {/* Admin-only: Recover from SUNO. Visible only on failed songs
                  for users with admin role. Most useful for songs that died
                  to the v2.9.18 race-condition or backend outages — SUNO
                  usually has the audio rendered, we just lost the link. */}
              {song.status === 'failed' && (user?.role === 'admin' || user?.role === 'super_admin') && (
                <Button
                  onClick={handleRecoverSong}
                  disabled={recovering}
                  variant="outline"
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                  data-testid="recover-song-detail-btn"
                  title="Admin: pull audio from SUNO using the stored task_id and mark this song completed"
                >
                  {recovering ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Recover from SUNO
                </Button>
              )}
              
              {/* Regenerate for successful songs — for covers, generates a new
                  variation against the same source MP3 (matches SUNO's web UI
                  re-render flow); for non-covers, navigates to create-song
                  with prefill so user can tweak parameters first. */}
              {song.status === 'completed' && song.audio_url && (
                <Button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  variant="outline"
                  className="border-white/20 text-zinc-300 hover:bg-white/10"
                  data-testid="regenerate-new-btn"
                >
                  {regenerating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  {song.is_cover ? 'Render Another Variation' : 'Regenerate New Version'}
                  <span className="ml-2 text-sm opacity-80">
                    • {renderCost} credit{renderCost === 1 ? '' : 's'}
                  </span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Library Settings Panel - show when song is public */}
        {showLibrarySettings && isPublic && !song.added_from_library && (
          <div className="glass-card p-6 border-purple-500/30">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-400" />
                Library Sharing Options
              </h3>
              <button
                onClick={() => setShowLibrarySettings(false)}
                className="p-1 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="space-y-4 mb-6">
              {/* Category Selection - Required */}
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <label className="text-white font-medium flex items-center gap-2 mb-2">
                  Category <span className="text-red-400">*</span>
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:border-purple-500 focus:outline-none"
                  data-testid="category-select"
                >
                  <option value="">Select a category...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                {!selectedCategory && (
                  <p className="text-xs text-yellow-400 mt-1">Category is required for public library</p>
                )}
              </div>
              
              {/* Download & Add Toggles - Side by Side */}
              <div className="flex gap-3">
                {/* Allow Download Toggle */}
                <div className="flex-1 flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Download className="h-4 w-4 text-zinc-400" />
                    <span className="text-white text-sm">Download</span>
                  </div>
                  <button
                    onClick={() => setAllowDownload(!allowDownload)}
                    className={`w-10 h-5 rounded-full transition-colors relative ${allowDownload ? 'bg-purple-500' : 'bg-zinc-600'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${allowDownload ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </div>
                
                {/* Allow Add to Library Toggle */}
                <div className="flex-1 flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <LibraryIcon className="h-4 w-4 text-zinc-400" />
                    <span className="text-white text-sm">Add to Library</span>
                  </div>
                  <button
                    onClick={() => setAllowAdd(!allowAdd)}
                    className={`w-10 h-5 rounded-full transition-colors relative ${allowAdd ? 'bg-purple-500' : 'bg-zinc-600'}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${allowAdd ? 'left-5' : 'left-0.5'}`} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Save and Remove Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleSaveLibraryOptions}
                disabled={savingLibraryOptions || !selectedCategory}
                className="flex-1 btn-primary"
              >
                {savingLibraryOptions ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Save Options
              </Button>
              <Button
                onClick={handleRemoveFromLibrary}
                disabled={togglingPublic}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                {togglingPublic ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Remove
              </Button>
            </div>
          </div>
        )}

        {/* Processing State — admin-curated 10s clip player.
            Falls back to a lightweight spinner if no batches uploaded.
            Stays mounted briefly after status flips so the outro clip
            can finish playing (outroPending). */}
        {(song.status === 'processing' || outroPending) && (
          <div className="glass-card p-6 sm:p-8 text-center">
            <RenderingClipPlayer
              elapsedSeconds={elapsedTime}
              estimatedMinutes={song?.suno_model === 'V4_5ALL' ? '1-2' : '2-4'}
              genre={song?.genre}
              prompt={song?.prompt}
              description={song?.description}
              songCompleted={song.status !== 'processing'}
              onAnimationDone={() => setOutroPending(false)}
            />
            <p className="text-zinc-400 text-sm mt-3 mb-1">
              {song.status === 'processing'
                ? 'The page will automatically update when complete.'
                : 'Wrapping up the final touches…'}
            </p>

            {song.status !== 'processing' ? (
              /* Song complete banner — shown while the outro plays out */
              <div
                className="mt-2 mb-2 mx-auto max-w-xl rounded-lg bg-emerald-500/20 border border-emerald-500/50 px-4 py-4 text-center"
                data-testid="song-complete-banner"
              >
                <p className="text-emerald-200 text-lg font-semibold mb-1">
                  🎉 Your song is complete!
                </p>
                <p className="text-emerald-100/80 text-sm">
                  Hang tight — the page will jump to your finished track in a moment.
                </p>
              </div>
            ) : (
              /* Free-to-browse banner: encourage users to keep using the site */
              <div className="mt-2 mb-2 mx-auto max-w-xl rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-left">
                <p className="text-emerald-300 text-sm font-medium mb-1">
                  💡 You don't have to wait here.
                </p>
                <p className="text-zinc-300 text-sm leading-relaxed">
                  Your song keeps generating in the background. Browse anywhere on the site
                  — even start another song — and we'll pop up a notification the moment it's ready.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button
                    size="sm"
                    onClick={() => navigate('/create-song')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="start-another-song-btn"
                  >
                    + Start Another Song
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/library')}
                    className="border-white/20 text-zinc-200 hover:bg-white/10"
                    data-testid="go-to-library-btn"
                  >
                    Browse Library
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="border-white/20 text-zinc-200 hover:bg-white/10"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            )}
            
            {/* Cancel option after 3 minutes — only while still processing */}
            {song.status === 'processing' && elapsedTime > 180 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-zinc-500 text-sm mb-3">Taking too long?</p>
                <Button
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={async () => {
                    const confirmed = window.confirm(
                      '⚠️ CANCEL SONG GENERATION?\n\n' +
                      'This song is still rendering on SUNO.\n\n' +
                      '• The render may still complete\n' +
                      '• If it does, it will be saved to "Cancelled Renders"\n' +
                      '• An admin can restore it to your account later\n\n' +
                      'Are you sure you want to cancel?'
                    );
                    if (!confirmed) return;
                    try {
                      await apiFetch(`/songs/${song.song_id}/cancel`, { method: 'POST' });
                      toast.success('Song cancelled - if it completes, admin can restore it');
                      navigate('/dashboard');
                    } catch (err) {
                      toast.error('Failed to cancel');
                    }
                  }}
                  data-testid="cancel-generation-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Cancel Generation
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Failed State */}
        {song.status === 'failed' && (() => {
          const errMsg = song.error || "Something went wrong while generating your song. Please try again.";
          const isContentFilter = /content filter|rejected|sensitive|content[ -]?policy|rephras/i.test(errMsg);
          return (
            <div className={`glass-card p-8 text-center ${isContentFilter ? 'border-amber-500/30' : 'border-red-500/20'}`}>
              <AlertCircle className={`h-12 w-12 mx-auto mb-4 ${isContentFilter ? 'text-amber-400' : 'text-red-500'}`} />
              <h3 className="text-xl font-bold text-white mb-2">
                {isContentFilter ? 'Prompt Rejected by Content Filter' : 'Generation Failed'}
              </h3>
              <p className="text-zinc-300 mb-3 max-w-xl mx-auto leading-relaxed">
                {errMsg}
              </p>

              {/* Show the rejected prompt so the user knows what to edit */}
              {isContentFilter && (song.prompt || song.theme || song.lyrics_prompt) && (() => {
                const rejectedText = song.prompt || song.theme || song.lyrics_prompt;
                const segments = highlightTriggers(rejectedText);
                const hasHighlights = segments.some((s) => typeof s !== 'string');
                return (
                  <div className="text-left bg-black/30 border border-amber-500/20 rounded-lg p-4 mb-4 max-w-xl mx-auto">
                    <div className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-1 flex items-center justify-between gap-2">
                      <span>Your rejected prompt</span>
                      {hasHighlights && (
                        <span
                          className="text-[10px] text-red-300 normal-case font-normal"
                          data-testid="trigger-detected-tag"
                        >
                          ⚠ likely triggers highlighted
                        </span>
                      )}
                    </div>
                    <div
                      className="text-sm text-zinc-200 whitespace-pre-wrap break-words leading-relaxed"
                      data-testid="rejected-prompt"
                    >
                      {segments}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-3 italic">
                      Watch out for: real artist names, song titles, brand names, explicit language,
                      and references to violence or other restricted topics.
                    </div>
                  </div>
                );
              })()}

              <div className="flex gap-3 justify-center flex-wrap">
                <Button onClick={handleRegenerate} className="btn-primary" data-testid="retry-failed-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {isContentFilter ? 'Edit & Retry' : 'Retry with Same Settings'}
                </Button>
                <Button onClick={() => navigate('/create-song')} variant="outline" className="border-white/20 text-zinc-300 hover:bg-white/10">
                  Start Fresh
                </Button>
              </div>
            </div>
          );
        })()}

        {/* Audio Player (Above Lyrics) */}
        {song.status === 'completed' && song.audio_url && (
          <div className="glass-card p-4 mt-8" data-testid="audio-player">
            <audio 
              ref={audioRef}
              src={song.audio_url}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={() => setIsPlaying(false)}
            />
            
            <div className="flex items-center gap-6">
              {/* Track Info */}
              <div className="flex items-center gap-3 min-w-0 flex-1 max-w-xs">
                <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-gradient-to-br from-primary/20 to-secondary/20 relative">
                  {song.image_url ? (
                    <img src={song.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="h-6 w-6 text-white/20" />
                    </div>
                  )}
                  {song.locked && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Lock className="h-4 w-4 text-yellow-400" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-white font-medium truncate flex items-center gap-1">
                    {song.title}
                    {song.locked && <span className="text-xs text-yellow-400">(50% Preview)</span>}
                  </p>
                  <p className="text-xs text-zinc-400 truncate">{song.tags?.split(',')[0]}</p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex-1 max-w-xl">
                <div className="flex items-center justify-center gap-4 mb-2">
                  <button
                    onClick={togglePlay}
                    className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 transition-transform"
                    data-testid="play-pause-btn"
                  >
                    {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                  </button>
                </div>
                
                {/* Progress Bar */}
                <div className="flex items-center gap-3">
                  <span className="text-xs text-zinc-400 w-10 text-right">{formatTime(currentTime)}</span>
                  <div 
                    className="progress-bar-container flex-1"
                    onClick={handleSeek}
                    data-testid="progress-bar"
                  >
                    <div 
                      className="progress-bar-fill"
                      style={{ width: `${(currentTime / duration) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-xs text-zinc-400 w-10">{formatTime(duration)}</span>
                </div>
              </div>

              {/* Volume */}
              <div className="hidden sm:block">
                <button
                  onClick={toggleMute}
                  className="text-zinc-400 hover:text-white transition-colors"
                  data-testid="mute-btn"
                >
                  {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Lyrics recovery: shown to the song's owner (or admin) when a
            completed song has no visible lyrics. Auto-recovery via our
            lyricists fires in the background 2 min after MusicGPT songs
            complete; this button is the manual escape hatch for:
              - Suno songs (auto-recovery doesn't run for those)
              - Songs marked "instrumental" that actually have vocals
              - Any completed song where lyrics never landed
            If the song IS truly wordless, the endpoint returns a
            harmless "song may be wordless" toast — no data harm. */}
        {!song.lyrics
          && !song.is_cover
          && song.status === 'completed'
          && song.audio_url
          && (user?.user_id === song.user_id || (user?.role === 'admin' || user?.role === 'super_admin'))
          && (
            <div className="glass-card p-4 mt-8 border border-amber-500/30 bg-amber-500/5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-amber-200 mb-0.5">
                    {song.instrumental ? 'Song marked instrumental — has singing?' : 'Lyrics not showing? Our lyricists can recover them.'}
                  </p>
                  <p className="text-xs text-zinc-400">
                    They&apos;ll listen to your song and transcribe the lyrics for you. Takes ~30 seconds.
                    {song.instrumental && ' If it really is instrumental, we\'ll say so.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={async (e) => {
                    const btn = e.currentTarget;
                    btn.disabled = true;
                    btn.textContent = 'Our lyricists are listening…';
                    try {
                      const { data } = await apiFetch(`/songs/${song.song_id}/recover-lyrics`, {
                        method: 'POST',
                      });
                      if (data?.ok && data?.lyrics_chars) {
                        toast.success(`Recovered ${data.lyrics_chars} characters of lyrics! Refreshing…`);
                        setTimeout(() => window.location.reload(), 1200);
                      } else {
                        toast.warning(data?.reason || 'Could not recover lyrics — the song may genuinely be wordless.');
                        btn.disabled = false;
                        btn.textContent = 'Recover lyrics';
                      }
                    } catch (err) {
                      // Fetch failed — most commonly a Cloudflare/nginx
                      // 100s proxy timeout kicked in while Whisper was
                      // still transcribing. The backend usually FINISHES
                      // and saves the lyrics after our request drops. So
                      // instead of failing outright, poll the song for
                      // ~90s to see if lyrics landed before showing the
                      // real error. Feels magical when it works.
                      btn.textContent = 'Still listening — checking every 5s…';
                      let recovered = false;
                      for (let i = 0; i < 18; i++) {
                        await new Promise((r) => setTimeout(r, 5000));
                        try {
                          const { data: s } = await apiFetch(`/songs/${song.song_id}`);
                          if (s?.lyrics && s.lyrics.length > 20) {
                            recovered = true;
                            toast.success(`Recovered ${s.lyrics.length} characters of lyrics! Refreshing…`);
                            setTimeout(() => window.location.reload(), 1000);
                            break;
                          }
                        } catch { /* keep polling */ }
                      }
                      if (!recovered) {
                        toast.error(err?.message || 'Recovery failed. Please try again.');
                        btn.disabled = false;
                        btn.textContent = 'Recover lyrics';
                      }
                    }
                  }}
                  className="px-4 py-2 text-xs rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/50 whitespace-nowrap disabled:opacity-70 disabled:cursor-wait"
                  data-testid="recover-lyrics-btn"
                >
                  Recover lyrics
                </button>
              </div>
            </div>
          )}

        {/* Lyrics Section — editable for the song owner (and admins) so
            users can fix Whisper transcription errors, add lyrics from
            scratch, or tweak section labels before clicking Regenerate /
            Render Another Variation. The regen pipeline reads
            `song.lyrics` from the DB at click-time, so saved edits flow
            into the next render. Shows for anyone with lyrics visible,
            plus for the owner/admin on any completed song even when
            lyrics are empty (so they can add them). */}
        {(() => {
          const canEditLyrics = user
            && (user.user_id === song.user_id
                || user.role === 'admin'
                || user.role === 'super_admin');
          const showLyricsCard = song.lyrics
            || song.is_cover
            || (canEditLyrics && song.status === 'completed');
          if (!showLyricsCard) return null;
          return (
          <div className="glass-card p-6 mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Lyrics</h3>
              {!editingLyrics && canEditLyrics && (
                <button
                  onClick={() => {
                    setLyricsDraft(song.lyrics || '');
                    setEditingLyrics(true);
                  }}
                  className="text-xs px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-white/5"
                  data-testid="edit-lyrics-btn"
                >
                  {song.lyrics ? 'Edit lyrics' : 'Add lyrics'}
                </button>
              )}
            </div>
            {editingLyrics ? (
              <>
                <textarea
                  value={lyricsDraft}
                  onChange={(e) => setLyricsDraft(e.target.value)}
                  rows={14}
                  className="w-full bg-black/40 border border-zinc-700 rounded-lg p-3 text-zinc-200 text-sm font-mono whitespace-pre-wrap focus:border-emerald-500 outline-none"
                  data-testid="lyrics-editor"
                  placeholder="Edit your lyrics — section labels like [Verse 1], [Chorus], [Bridge] are honoured by SUNO when you regenerate."
                />
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={async () => {
                      try {
                        const r = await apiFetch(`/songs/${song.song_id}/lyrics`, {
                          method: 'PATCH',
                          body: JSON.stringify({ lyrics: lyricsDraft }),
                        });
                        toast.success(song.is_cover
                          ? 'Lyrics saved — Render Another Variation will use these.'
                          : 'Lyrics saved — Regenerate New Version will use these.');
                        setSong((prev) => prev ? { ...prev, lyrics: r?.data?.lyrics ?? lyricsDraft } : prev);
                        setEditingLyrics(false);
                      } catch (err) {
                        toast.error(err.message || 'Could not save lyrics');
                      }
                    }}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="save-lyrics-btn"
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingLyrics(false);
                      setLyricsDraft('');
                    }}
                    className="border-zinc-700 text-zinc-300"
                    data-testid="cancel-lyrics-btn"
                  >
                    Cancel
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Tip: SUNO honours section markers like <code className="text-zinc-400">[Verse 1]</code>,
                  <code className="ml-1 text-zinc-400">[Chorus]</code>,
                  <code className="ml-1 text-zinc-400">[Bridge]</code>,
                  <code className="ml-1 text-zinc-400">[Outro]</code> on their own lines.
                </p>
              </>
            ) : (
              <div className="bg-black/30 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-zinc-300 whitespace-pre-wrap text-sm font-mono">{song.lyrics || 'No lyrics yet — click Add lyrics to type them in, or use Recover lyrics above to auto-transcribe.'}</pre>
              </div>
            )}
          </div>
          );
        })()}
          </div>

          {/* Songs Sidebar - Dynamic height */}
          <SongsSidebar currentSongId={songId} refreshTrigger={sidebarRefresh} maxHeight={sidebarHeight} />
        </div>
      </main>
      
      {/* v3.0.134 — Add to Playlist dialog */}
      <AddToPlaylistDialog
        open={showPlaylistDialog}
        onOpenChange={setShowPlaylistDialog}
        songIds={song ? [song.song_id] : []}
      />

      {/* Video Creation Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card p-6 max-w-md w-full">
            {!videoStatus ? (
              <>
                <h3 className="text-xl font-bold text-white mb-2">Create MP4 Video</h3>
                <p className="text-zinc-400 text-sm mb-6">
                  Generate an MP4 with your album cover + full song. Perfect for Facebook, TikTok, Instagram, and download for offline playback and other sharing!
                </p>
                
                {/* Preview */}
                <div className="mb-6 flex justify-center">
                  <div className={`bg-zinc-800 rounded-lg overflow-hidden ${
                    videoOrientation === 'portrait' ? 'w-24 h-40' :
                    videoOrientation === 'square' ? 'w-32 h-32' : 'w-40 h-24'
                  }`}>
                    {song.image_url ? (
                      <img src={song.image_url} alt={song.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music className="h-8 w-8 text-zinc-600" />
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Orientation Selection */}
                <div className="mb-6">
                  <label className="text-white text-sm font-medium mb-3 block">Choose Format</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: 'square', label: 'Square', desc: 'Facebook/Instagram', icon: '⬜' },
                      { id: 'portrait', label: 'Portrait', desc: 'TikTok/Reels', icon: '📱' },
                      { id: 'landscape', label: 'Landscape', desc: 'YouTube', icon: '🖥️' }
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setVideoOrientation(opt.id)}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          videoOrientation === opt.id 
                            ? 'border-primary bg-primary/10' 
                            : 'border-zinc-700 hover:border-zinc-500'
                        }`}
                      >
                        <span className="text-xl">{opt.icon}</span>
                        <p className="text-white text-sm font-medium mt-1">{opt.label}</p>
                        <p className="text-[10px] text-zinc-500">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowVideoModal(false)}
                    variant="outline"
                    className="flex-1 border-zinc-700 text-zinc-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateVideo}
                    disabled={creatingVideo}
                    className="flex-1 bg-yellow-800 hover:bg-yellow-900 text-white"
                  >
                    {creatingVideo ? (
                      <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating...</>
                    ) : (
                      <><Video className="h-4 w-4 mr-2" /> Create Video (Free)</>
                    )}
                  </Button>
                </div>
              </>
            ) : videoStatus.status === 'pending' || videoStatus.status === 'processing' ? (
              <div className="text-center py-8">
                <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Creating Your Video...</h3>
                <p className="text-zinc-400">This may take a minute or two.</p>
              </div>
            ) : videoStatus.status === 'completed' ? (
              <div className="text-center py-4">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Video Ready!</h3>
                <p className="text-zinc-400 mb-6">Download and share on social media.</p>
                <div className="flex gap-3 justify-center flex-wrap">
                  <a 
                    href={videoStatus.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn-secondary inline-flex items-center gap-2"
                  >
                    <Play className="h-4 w-4" /> Preview
                  </a>
                  <button 
                    onClick={async () => {
                      try {
                        const response = await fetch(videoStatus.video_url);
                        const blob = await response.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = blobUrl;
                        link.download = `${song?.title || 'video'}.mp4`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(blobUrl);
                      } catch (err) {
                        console.error('Download error:', err);
                      }
                    }}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" /> Download MP4
                  </button>
                </div>
                <Button
                  onClick={() => {
                    setShowVideoModal(false);
                    setVideoStatus(null);
                    setVideoId(null);
                  }}
                  variant="ghost"
                  className="mt-4 text-zinc-400"
                >
                  Close
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Generation Failed</h3>
                <p className="text-red-400 mb-4">{videoStatus.error || 'Something went wrong'}</p>
                <Button
                  onClick={() => {
                    setVideoStatus(null);
                    setVideoId(null);
                    setCreatingVideo(false);
                  }}
                  className="btn-primary"
                >
                  Try Again
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
